import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

// Material
import { makeStyles } from "@material-ui/core/styles";

// Config
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import { capturePages } from "../../config/values";
import styles from "../../config/styles";

// Actions
import * as injuryActions from "../../state/injuries/actions";
import * as physicalDamageActions from "../../state/physicalDamage/actions";

// Components
import AddInjuryImage from "../AddInjuryImage";
import AddPhysicalDamageImage from "../AddPhysicalDamageImage";
import CaseMediaGallery from "../CaseMediaGallery";
import EditMediaImage from "./EditMediaImage";
import Text from "../Text";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column"
  },
  heading: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1)
  },
  imagesBox: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
    width: "100%",
    padding: theme.spacing(2, 0, 2),
    border: styles.values.border,
    borderRadius: theme.shape.borderRadius
  }
}));

const EditMediaGallery = ({
  caseId,
  imageHeaders,
  images,
  isDeletingImage,
  itemId,
  loadingHeaders,
  page
}) => {
  const isInjury = page === capturePages.INJURY;
  const actions = isInjury ? injuryActions : physicalDamageActions;
  const idParamName = isInjury ? "injuryId" : "physicalDamageId";
  const AddImage = isInjury ? AddInjuryImage : AddPhysicalDamageImage;

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(0);
  const { trackException } = useAppInsights();
  const classes = useStyles();
  const dispatch = useDispatch();

  const handleDeleteImage = filename => () => {
    dispatch(
      actions.deleteImage({
        caseId,
        [idParamName]: itemId,
        filename,
        onFail: () =>
          trackException(
            `Failed to delete file: ${filename} from ${idParamName}: ${itemId} in case: ${caseId}`
          )
      })
    );
  };

  const handleNavigate = forward => () => {
    let next = forward ? selected + 1 : selected - 1;
    if (next < 0) next = images.length - 1;
    if (next === images.length) next = 0;
    setSelected(next);
  };

  const handleCloseGallery = () => setOpen(false);
  const handleSelect = index => () => {
    setSelected(index);
    setOpen(true);
  };

  const handleAuthError = () => {
    const { filename } = images[selected];
    dispatch(
      actions.getImage({
        caseId,
        [idParamName]: itemId,
        filename,
        index: selected,
        forceReplace: true
      })
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.heading}>
        <Text type="subtitleBold" text="editInjuryPage.imagesTitle" />
        <AddImage />
      </div>
      {images && (
        <div className={classes.imagesBox}>
          {images.map((image, index) => (
            <EditMediaImage
              handleDeleteImage={handleDeleteImage}
              handleSelect={handleSelect}
              image={image}
              imageHeaders={imageHeaders}
              imageRequest={{ caseId, [idParamName]: itemId }}
              index={index}
              isDeletingImage={isDeletingImage}
              key={image.filename}
              loadingHeaders={loadingHeaders}
              page={page}
            />
          ))}
        </div>
      )}
      {images && images.length > 0 && (
        <CaseMediaGallery
          data={images[selected]}
          onAuthError={handleAuthError}
          onClose={handleCloseGallery}
          onNext={handleNavigate(true)}
          onPrev={handleNavigate(false)}
          open={open}
        />
      )}
    </div>
  );
};

EditMediaGallery.propTypes = {
  caseId: PropTypes.string.isRequired,
  imageHeaders: PropTypes.shape({}).isRequired,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      filename: PropTypes.string,
      isProcessing: PropTypes.bool
    })
  ).isRequired,
  isDeletingImage: PropTypes.string,
  itemId: PropTypes.string.isRequired,
  loadingHeaders: PropTypes.shape({}).isRequired,
  page: PropTypes.oneOf(Object.values(capturePages)).isRequired
};

EditMediaGallery.defaultProps = {
  isDeletingImage: ""
};

export default React.memo(EditMediaGallery);
